import {
  type AnimationAction,
  AnimationClip,
  type AnimationMixer,
  AnimationUtils,
  LoopRepeat,
  type SkinnedMesh
} from 'three'

/**
 * Lightweight additive-layer system on top of the existing single-clip
 * AnimationMixer. The base animation drives the rig as today; layered clips
 * are converted to additive deltas (via `AnimationUtils.makeClipAdditive`)
 * and played on the same mixer with adjustable per-layer weight.
 *
 * Example use:
 *  - base = walk cycle (weight 1.0, locked on)
 *  - layer = breathing on top (weight 0.5, additive)
 *  - layer = waving right arm on top (weight 1.0, additive)
 *
 * The manager holds AnimationActions keyed by a display name so the UI can
 * toggle/adjust them without remembering action references. `dispose()`
 * tears all of them down (called from the existing mixer reset path).
 */

export interface ActiveLayer {
  display_name: string
  weight: number
}

export class AnimationLayerManager {
  private active_layers: Map<string, AnimationAction> = new Map()

  /**
   * Add an additive layer derived from `source_clip`. The clip is cloned and
   * converted to additive deltas (mutates the clone, not the original). If a
   * layer with the same name is already active it's replaced.
   */
  public add_layer (
    mixer: AnimationMixer,
    skinned_mesh: SkinnedMesh,
    source_clip: AnimationClip,
    weight: number = 1.0
  ): void {
    this.remove_layer(source_clip.name)
    const layer_clip = source_clip.clone()
    layer_clip.name = `${source_clip.name} [layer]`
    AnimationUtils.makeClipAdditive(layer_clip)
    const action = mixer.clipAction(layer_clip, skinned_mesh)
    action.setEffectiveWeight(weight)
    action.setLoop(LoopRepeat, Infinity)
    action.play()
    this.active_layers.set(source_clip.name, action)
  }

  public set_layer_weight (clip_name: string, weight: number): void {
    const action = this.active_layers.get(clip_name)
    if (action !== undefined) action.setEffectiveWeight(weight)
  }

  public remove_layer (clip_name: string): void {
    const action = this.active_layers.get(clip_name)
    if (action === undefined) return
    action.stop()
    // Free the cached additive clip so the mixer doesn't keep replaying it.
    const root = action.getRoot()
    const clip = action.getClip()
    if (root !== undefined) {
      // No public mixer.uncacheClip in the typed API for some Three versions;
      // calling stop is the documented way to detach. The clip itself is the
      // throwaway clone created by makeClipAdditive — letting it GC is fine.
      void clip
    }
    this.active_layers.delete(clip_name)
  }

  public list_active_layers (): ActiveLayer[] {
    const out: ActiveLayer[] = []
    this.active_layers.forEach((action, display_name) => {
      out.push({ display_name, weight: action.getEffectiveWeight() })
    })
    return out
  }

  public has_layer (clip_name: string): boolean {
    return this.active_layers.has(clip_name)
  }

  public dispose (): void {
    this.active_layers.forEach((action) => { action.stop() })
    this.active_layers.clear()
  }
}
